import { useCopyToClipboard } from 'Modules/softphone/components/SoftphoneDevTools/hooks';
import { Check, Copy } from 'lucide-react';
import { DevToolsSegmentedControl } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsSegmentedControl';
import type { DebugRecordMediaProps } from 'Modules/softphone/components/SoftphoneDevTools/DevToolsTabs/DebugTab/DebugRecord/DebugRecordMedia/index.types';
import {
  SURFACE,
} from 'Modules/softphone/components/SoftphoneDevTools/styles';
import React, { useMemo, useState } from 'react';

type MediaView = 'local' | 'remote' | 'ice';

/** Stable identity, so a record with no candidates does not re-memo forever. */
const EMPTY_CANDIDATES = { local: [] as string[], remote: [] as string[] };

/**
 * DebugRecordMedia - Local SDP, remote SDP and the ICE candidates
 *
 * One body at a time rather than three stacked blocks: an SDP is 30 lines
 * and the reader is comparing two of them, not skimming all three.
 */
export const DebugRecordMedia: React.FC<DebugRecordMediaProps> = ({ info }) => {
  const [view, setView] = useState<MediaView>('local');
  const { copied, copy } = useCopyToClipboard();

  const { local: localCandidates, remote: remoteCandidates } =
    info.iceCandidates ?? EMPTY_CANDIDATES;

  const bodies = useMemo<Record<MediaView, { title: string; body: string }>>(
    () => ({
      local: { title: 'Local SDP', body: info.localSdp ?? '' },
      remote: { title: 'Remote SDP', body: info.remoteSdp ?? '' },
      ice: {
        title: 'ICE candidates',
        body: [
          localCandidates.length > 0
            ? `# local (${localCandidates.length})\n${localCandidates.join('\n')}`
            : '# local (0)',
          remoteCandidates.length > 0
            ? `# remote (${remoteCandidates.length})\n${remoteCandidates.join('\n')}`
            : '# remote (0)',
        ].join('\n\n'),
      },
    }),
    [info.localSdp, info.remoteSdp, localCandidates, remoteCandidates]
  );

  const current = bodies[view];
  const candidateCount = localCandidates.length + remoteCandidates.length;

  return (
    <div className="flex flex-col gap-3">
      <DevToolsSegmentedControl<MediaView>
        label="Media detail"
        variant="inline"
        value={view}
        onChange={setView}
        options={[
          { value: 'local', label: 'Local SDP' },
          { value: 'remote', label: 'Remote SDP' },
          { value: 'ice', label: 'ICE candidates' },
        ]}
      />

      <div className={SURFACE.card}>
        <div className="flex items-center gap-2 border-b border-zinc-800 px-3 py-2.5">
          <span className="text-xs font-semibold text-zinc-200">
            {current.title}
          </span>
          <div className="flex-1" />
          <span className="font-sp-mono text-[10px] text-zinc-400">
            {view === 'ice'
              ? `${candidateCount} candidates`
              : current.body
                ? `${current.body.split('\n').length} lines`
                : 'empty'}
          </span>
          {current.body && (
            <button
              type="button"
              onClick={() => copy(current.body)}
              className="inline-flex items-center gap-1 rounded-[5px] border border-zinc-800 px-[7px] py-[3px] text-[10.5px] text-zinc-400 transition-colors hover:border-emerald-800 hover:text-emerald-400 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-400/40"
            >
              {copied ? (
                <Check className="h-2.5 w-2.5 text-emerald-400" />
              ) : (
                <Copy className="h-2.5 w-2.5" />
              )}
              {copied ? 'Copied' : 'Copy'}
            </button>
          )}
        </div>

        {current.body ? (
          <pre className="max-h-[340px] overflow-auto whitespace-pre bg-zinc-950 px-3 py-[11px] font-sp-mono text-[10.5px] leading-[1.65] text-zinc-300">
            {current.body}
          </pre>
        ) : (
          <p className="px-3 py-4 text-center text-[11.5px] text-zinc-400">
            Not captured for this leg.
          </p>
        )}
      </div>

      <p className="rounded-[10px] border border-zinc-800 bg-zinc-900 px-3 py-2.5 text-[11px] leading-[1.55] text-zinc-400">
        <span className="font-sp-mono text-amber-400">ice-ufrag</span>,{' '}
        <span className="font-sp-mono text-amber-400">ice-pwd</span> and{' '}
        <span className="font-sp-mono text-amber-400">fingerprint</span> are
        redacted in exports. IPs and ICE candidates are never masked — they are
        the diagnosis.
      </p>
    </div>
  );
};
